import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import moment from 'moment-timezone';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ICalendarEventsDTO, ISystemSettingsDTO } from 'src/api/dtos';
import CalendarRepository from 'src/api/repositories/ess/v1/calendar.repository';
import { AbsenceNotesTooltip } from 'src/features/absences/AbsenceNotesTooltip';
import { getAvailabilityTitle } from 'src/features/availabilities/availabilities.utils';
import { CalendarAbsenceRequestModal, CalendarScheduledAbsenceModal, CalendarShiftModal } from 'src/features/calendar';
import { CalendarEventCategoryType } from 'src/features/calendar/calendar.types';
import { getRoute } from 'src/routes';
import {
  CalendarIcon,
  ChatSquareIcon,
  DailyCalendar,
  DailyEventType,
  DateRangeSelector,
  DepartmentIcon,
  EmptyState,
  ServerError500Icon,
  SuitcaseIcon,
  Tooltip,
} from 'src/shared/components';
import { DATE_TIME_FORMAT, DATE_MONTH_FORMAT, MONTH_YEAR_FORMAT } from 'src/shared/constants';
import {
  AvailabilityStatusEnums,
  FeaturesEnums,
  NotificationFlagsEnums,
  PermissionsEnums,
  ShiftSwapStateEnums,
} from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useFeatures } from 'src/shared/hooks';
import { TranslationFunction, useTranslate } from 'src/shared/providers';
import { DateRangeType } from 'src/shared/types';
import { formatTime, isDateWithinDateRange, mapAbsenceStatusStyle } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { getTimeFormat } from 'src/store/user/user.selectors';

export const WeeklyCalendarEvents: FC = memo(() => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { settings, info } = useSelector((state: RootState) => state.user);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventCategoryType | undefined>();
  const timeFormat = useSelector(getTimeFormat);

  const [currentDateSelector, setCurrentDateSelector] = useState<{ fromDate: string; toDate: string }>({
    fromDate: moment().startOf('week').format(),
    toDate: moment().endOf('week').format(),
  });

  const {
    data: events,
    isLoading,
    isFetching,
    error,
  } = CalendarRepository.endpoints.useGetEventsQuery({
    from: currentDateSelector.fromDate,
    to: currentDateSelector.toDate,
  });

  const loading = isLoading || isFetching;

  const hasAllocationPermissions = useFeatures([
    FeaturesEnums.DepartmentAllocation,
    FeaturesEnums.JobCosting,
    FeaturesEnums.SchedulingLabourAllocation,
  ]);

  const hasJobCosting = useFeatures([FeaturesEnums.JobCosting]);

  const hasShiftSwapping = useFeatures(FeaturesEnums.ShiftSwapping);

  const mapDays: Map<moment.Moment, DailyEventType[] | undefined> = useMemo(() => {
    const map = new Map<moment.Moment, DailyEventType[] | undefined>();

    const startDate = currentDateSelector.fromDate;
    const endDate = currentDateSelector.toDate;

    const currentDate = moment(startDate || endDate).startOf('day');
    const lastDate = moment(endDate || startDate).endOf('day');

    do {
      const date = currentDate.clone();

      const dailyEvents = events
        ? getEventsValues(
            info?.employeeId,
            date,
            events,
            settings,
            setSelectedEvent,
            hasAllocationPermissions,
            hasJobCosting,
            hasShiftSwapping,
            t,
            timeFormat,
          )
        : undefined;

      map.set(date, dailyEvents);
    } while (currentDate.add(1, 'days').diff(lastDate) < 0);

    return map;
  }, [info?.employeeId, events]);

  const formatter = useCallback((dateRange: DateRangeType) => {
    const today = moment();
    const { startDate, endDate } = dateRange;
    const startDateMoment = moment(startDate);
    const endDateMoment = moment(endDate);

    const date = `${startDateMoment.format(DATE_MONTH_FORMAT)} - ${endDateMoment.format(DATE_MONTH_FORMAT)}`;

    if (isDateWithinDateRange(today, { startDate, endDate })) {
      return `${startCase(toLower(t('global.currentWeek')))}: ${date}`;
    }

    const lastWeek = today.clone().subtract(7, 'days');

    if (isDateWithinDateRange(lastWeek, { startDate, endDate })) {
      return `${startCase(toLower(t('global.lastWeek')))}: ${date}`;
    }

    const nextWeek = today.clone().add(7, 'days');

    if (isDateWithinDateRange(nextWeek, { startDate, endDate })) {
      return `${startCase(toLower(t('global.nextWeek')))}: ${date}`;
    }

    return date;
  }, []);

  const handleDateRangeChange = (dateRange: DateRangeType) => {
    setCurrentDateSelector({ fromDate: dateRange.startDate, toDate: dateRange.endDate });
  };

  const handleNavigateToCalendarPage = () => {
    const month = moment(currentDateSelector.toDate).startOf('month').format(MONTH_YEAR_FORMAT);

    const search = qs.stringify({ month }, { arrayFormat: 'brackets' });
    navigate({ pathname: getRoute('calendar'), search });
  };

  if (error) {
    return (
      <div className="mb-4">
        <h3 className="mb-0 text-capitalize">{t('calendar.myCalendar')}</h3>
        <EmptyState message={t('global.oopsAnErrorOccurred')} icon={<ServerError500Icon />} />
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <div className="d-flex align-items-center">
          <h3 className="mb-0 text-capitalize">{t('calendar.myCalendar')}</h3>
          <DateRangeSelector
            dateRange={{ startDate: currentDateSelector.fromDate, endDate: currentDateSelector.toDate }}
            formatter={formatter}
            onDateRangeChange={handleDateRangeChange}
            className="mx-4"
          />
          <Button
            id="view-full-calendar"
            variant="contained"
            color="primary"
            startIcon={<CalendarIcon size={20} />}
            onClick={handleNavigateToCalendarPage}
          >
            {t('calendar.viewFullCalendar')}
          </Button>
        </div>
        <div id="weekly-calendar" className="w-100 d-flex align-items-center justify-content-start overflow-auto mt-3">
          {Array.from(mapDays).map(([date, evts], index) => {
            if (loading) {
              return (
                <Skeleton
                  key={`daily-calendar-loading-${index}`}
                  className="me-3 rounded border"
                  variant="rectangular"
                  animation="wave"
                  width={198}
                  height={220}
                />
              );
            }

            return <DailyCalendar key={`weekly-calendar-events-${index}`} className="me-3" date={date} events={evts} />;
          })}
        </div>
      </div>
      {selectedEvent?.type === 'schedule' && (
        <CalendarShiftModal item={selectedEvent.value} open onClose={() => setSelectedEvent(undefined)} />
      )}
      {selectedEvent?.type === 'shiftPickup' && (
        <CalendarShiftModal item={selectedEvent.value} open onClose={() => setSelectedEvent(undefined)} isPickup />
      )}
      {selectedEvent?.type === 'absenceRequest' && (
        <CalendarAbsenceRequestModal
          employeeId={info?.employeeId}
          item={selectedEvent.value}
          open
          onClose={() => setSelectedEvent(undefined)}
        />
      )}
      {selectedEvent?.type === 'scheduledAbsence' && (
        <CalendarScheduledAbsenceModal
          employeeId={info?.employeeId}
          item={selectedEvent.value}
          settings={settings}
          open
          onClose={() => setSelectedEvent(undefined)}
        />
      )}
    </>
  );
});

const getEventsValues = (
  employeeId: number | undefined,
  date: moment.Moment,
  events: ICalendarEventsDTO,
  settings: ISystemSettingsDTO | null,
  onClick: (event: CalendarEventCategoryType) => void,
  hasAllocationPermissions: boolean,
  hasJobCosting: boolean,
  hasShiftSwapping: boolean,
  t: TranslationFunction,
  timeFormat: string | undefined,
): DailyEventType[] => {
  const dailyAbsenceRequests = events.absenceRequests
    .filter(({ startDate, endDate, statusId }) => {
      return (
        isDateWithinDateRange(date, { startDate, endDate }) &&
        (statusId === NotificationFlagsEnums.INITIAL || statusId === NotificationFlagsEnums.REJECTED)
      );
    })
    .map<DailyEventType>((absenceRequest) => {
      const { startTime, endTime, isPartialDay, absenceTypeName, notes } = absenceRequest;
      const formattedStartTime = startTime ? formatTime(startTime, timeFormat, DATE_TIME_FORMAT) : undefined;
      const formattedEndTime = endTime ? formatTime(endTime, timeFormat, DATE_TIME_FORMAT) : undefined;

      const absenceValue = (
        <div className="d-flex align-items-center">
          <span className="me-1">{absenceTypeName}</span>
          <AbsenceNotesTooltip employeeId={employeeId} notes={notes} />
        </div>
      );

      return {
        type: mapAbsenceStatusStyle[absenceRequest.statusId],
        time: startTime ? moment(startTime).format() : null,
        title: isPartialDay ? `${formattedStartTime} - ${formattedEndTime}` : absenceValue,
        description: isPartialDay ? absenceValue : undefined,
        onClick: () => onClick({ value: absenceRequest, type: 'absenceRequest' }),
      };
    });

  const dailyScheduledAbsences = events.scheduledAbsences
    .filter(({ startDate, endDate }) => {
      return isDateWithinDateRange(date, { startDate, endDate });
    })
    .map<DailyEventType>((absenceSchedule) => {
      const { startTime, endTime, timeCategoryName, notes } = absenceSchedule;
      const formattedStartTime = startTime ? formatTime(startTime, timeFormat, DATE_TIME_FORMAT) : undefined;
      const formattedEndTime = endTime ? formatTime(endTime, timeFormat, DATE_TIME_FORMAT) : undefined;

      const absenceValue = (
        <div className="d-flex align-items-center">
          <span className="me-1">{timeCategoryName}</span>
          <AbsenceNotesTooltip employeeId={employeeId} notes={notes} />
        </div>
      );

      return {
        type: 'success',
        time: startTime ? moment(startTime).format() : null,
        title: startTime ? `${formattedStartTime} - ${formattedEndTime}` : absenceValue,
        description: startTime ? absenceValue : undefined,
        onClick: () => onClick({ value: absenceSchedule, type: 'scheduledAbsence' }),
      };
    });

  const dailyShifts = events.schedules
    .filter(({ startDate }) => {
      return date.isSame(startDate, 'day');
    })
    .map<DailyEventType>((shift) => {
      const { startDate, endDate, labourAllocations, isUnpublished, notes, shiftSwap } = shift;
      const formattedStartTime = startDate ? formatTime(startDate, timeFormat, DATE_TIME_FORMAT) : undefined;
      const formattedEndTime = endDate ? formatTime(endDate, timeFormat, DATE_TIME_FORMAT) : undefined;
      const shiftSwapEnabled = !!settings?.isShiftSwapEnabled && hasShiftSwapping;
      const icon = hasJobCosting ? <SuitcaseIcon /> : <DepartmentIcon />;
      const unpublished = settings?.isPublishScheduleRestrictViewEnabled && isUnpublished;

      const getShiftDescription = () => {
        if (!shiftSwapEnabled) {
          return t('calendar.schedule');
        }

        switch (shiftSwap?.state) {
          case ShiftSwapStateEnums.PENDING_SHIFT_DROP:
            return `(${t('shiftSwaps.awaitingPickup')})`;
          case ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL:
            return `(${t('shiftSwaps.pendingApproval')})`;
          case ShiftSwapStateEnums.NONE:
          case ShiftSwapStateEnums.DISABLED:
          case ShiftSwapStateEnums.SHIFT_PICKED_UP:
          default:
            return t('calendar.schedule');
        }
      };

      const shiftValue = (
        <div className="d-flex align-items-center">
          <span className="me-1 calendar-title">
            {formattedStartTime} - {formattedEndTime}
          </span>
          <ComponentWithPermissions permissions={PermissionsEnums.SS_NOTES_VIEW}>
            {notes && (
              <Tooltip title={notes}>
                <IconButton className="p-0 ms-1" size="small" color="inherit">
                  <ChatSquareIcon size={12} />
                </IconButton>
              </Tooltip>
            )}
          </ComponentWithPermissions>
        </div>
      );

      return {
        type: 'info',
        time: startDate ? moment(startDate).format() : null,
        title: shiftValue,
        description: getShiftDescription(),
        icon: hasAllocationPermissions && !!labourAllocations.length ? icon : undefined,
        unpublished,
        onClick: () => onClick({ value: shift, type: 'schedule' }),
      };
    });

  const dailyShiftPickups = events.shiftPickups
    .filter(({ startDate }) => {
      return date.isSame(startDate, 'day');
    })
    .map<DailyEventType>((shift) => {
      const { startDate, endDate, labourAllocations, isUnpublished, notes, shiftSwap } = shift;
      const unpublished = settings?.isPublishScheduleRestrictViewEnabled && isUnpublished;
      const shiftSwapEnabled = !!settings?.isShiftSwapEnabled && hasShiftSwapping;
      const formattedStartTime = startDate ? formatTime(startDate, timeFormat, DATE_TIME_FORMAT) : undefined;
      const formattedEndTime = endDate ? formatTime(endDate, timeFormat, DATE_TIME_FORMAT) : undefined;
      const icon = hasJobCosting ? <SuitcaseIcon /> : <DepartmentIcon />;

      const getShiftPickupDescription = () => {
        if (!shiftSwapEnabled) {
          return;
        }

        switch (shiftSwap?.state) {
          case ShiftSwapStateEnums.PENDING_SHIFT_DROP:
            return t('shiftSwaps.availableForPickup');
          case ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL:
            return t('shiftSwaps.pendingApproval');
          case ShiftSwapStateEnums.NONE:
          case ShiftSwapStateEnums.DISABLED:
          case ShiftSwapStateEnums.SHIFT_PICKED_UP:
          default:
          // No default;
        }
      };

      const shiftValue = (
        <div className="d-flex align-items-center">
          <span className="me-1 calendar-title">
            {formattedStartTime} - {formattedEndTime}
          </span>
          <ComponentWithPermissions permissions={PermissionsEnums.SS_NOTES_VIEW}>
            {notes && (
              <Tooltip title={notes}>
                <IconButton className="p-0 ms-1" size="small" color="inherit">
                  <ChatSquareIcon size={12} />
                </IconButton>
              </Tooltip>
            )}
          </ComponentWithPermissions>
        </div>
      );

      return {
        type: shiftSwap?.state === ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL ? 'pending' : 'danger',
        time: startDate ? moment(startDate).format() : null,
        title: shiftValue,
        description: getShiftPickupDescription(),
        icon: hasAllocationPermissions && !!labourAllocations.length ? icon : undefined,
        unpublished,
        onClick: () => onClick({ value: shift, type: 'shiftPickup' }),
      };
    });

  const dailyHoliday = events.holidays
    .filter(({ date: holidayDate }) => {
      return date.isSame(holidayDate, 'day');
    })
    .map<DailyEventType>((holiday) => {
      return { type: 'default', time: null, title: holiday.description };
    });

  const groupedDailyAvailabilities = Object.entries(
    groupBy(
      events.availabilities.filter(
        ({ date: availabilityDate }) =>
          date.isSame(availabilityDate, 'day') &&
          // Don't show availability if user has a scheduled absence on the same day
          !events.scheduledAbsences.some((scheduledAbsence) =>
            moment(scheduledAbsence.startDate).isSame(availabilityDate, 'days'),
          ),
      ),
      'date',
    ),
  ).map<DailyEventType>(([, dateAvailabilities]) => {
    const orderedDateAvailabilities = orderBy(dateAvailabilities, ['startTimeInSeconds'], ['asc']);

    const first =
      orderedDateAvailabilities.find(({ status }) => status === AvailabilityStatusEnums.NOT_AVAILABLE) ??
      orderedDateAvailabilities[0];

    const hasMany = orderedDateAvailabilities.length > 1;

    const { startTimeInSeconds, endTimeInSeconds, date: availabilityDate, status } = first;

    const type = status === AvailabilityStatusEnums.AVAILABLE ? 'muted' : 'error';
    const time = availabilityDate ? moment(availabilityDate).format() : null;
    const title = `${getAvailabilityTitle(startTimeInSeconds, endTimeInSeconds, timeFormat, t)} ${
      hasMany ? '...' : ''
    }`;
    const description =
      status === AvailabilityStatusEnums.AVAILABLE ? t('availability.available') : t('availability.unavailable');

    return {
      type,
      time,
      title,
      description,
      tooltip: hasMany ? (
        <Stack>
          {orderedDateAvailabilities.map(({ startTimeInSeconds, endTimeInSeconds, status }, index) => {
            return (
              <div
                key={index}
                className="d-flex align-items-center justify-content-between w-100"
                style={{ minWidth: 0 }}
              >
                <div className="event-title fw-500 me-1">
                  {getAvailabilityTitle(startTimeInSeconds, endTimeInSeconds, timeFormat, t)}
                </div>
                <div className="event-description flex-shrink-0 fw-400">
                  {status === AvailabilityStatusEnums.AVAILABLE
                    ? t('availability.available')
                    : t('availability.unavailable')}
                </div>
              </div>
            );
          })}
        </Stack>
      ) : null,
    };
  });

  return orderBy(
    [
      ...dailyAbsenceRequests,
      ...dailyScheduledAbsences,
      ...dailyShifts,
      ...dailyShiftPickups,
      ...dailyHoliday,
      ...groupedDailyAvailabilities,
    ],
    [(item) => !item.time, 'time'],
    ['desc', 'asc'],
  );
};
